import React, { useState } from 'react';
import { Upload, FileText, Loader2, AlertCircle, Download, CheckCircle2 } from 'lucide-react';
import { apiUrl } from '@/config';

const API_URL = apiUrl('/envio-publicitario/csv');
// API key: usa env si existe; si no, fallback a "prueba" (tu backend valida con validate_api_key)
const API_KEY = (import.meta as any)?.env?.VITE_MCP_API_KEY ?? 'mcp_93644c7fe708e1800577edbc2d47d3ad';

export function EnvioMasivo() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');

  const readFileAsUTF8 = async (file: File): Promise<File> => {
    const buffer = await file.arrayBuffer();
    try {
      new TextDecoder('utf-8', { fatal: true }).decode(buffer);
      return file;
    } catch {
      const latin1Text = new TextDecoder('windows-1252').decode(buffer);
      const utf8Blob = new Blob([latin1Text], { type: 'text/csv;charset=utf-8;' });
      return new File([utf8Blob], file.name, { type: 'text/csv' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setMensaje('Debes seleccionar un archivo CSV.');
      return;
    }

    setLoading(true);
    setMensaje('');

    const formData = new FormData();
    const safeFile = await readFileAsUTF8(file);
    // El backend acepta el campo 'file' (y opcionalmente 'csv'); con esto basta
    formData.append('file', safeFile);

    const headers: HeadersInit = { 'x-api-key': API_KEY };
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers,               // no pongas Content-Type; fetch lo arma para FormData
        body: formData,
      });

      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json()
                                                   : { detalle: (await response.text()) || 'Respuesta vacía del servidor' };

      if (!response.ok) throw new Error(data?.detalle || `Error ${response.status}`);

      setMensaje(`✅ ${data.detalle}`);
    } catch (error: any) {
      setMensaje(`💥 Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Ajustado al esquema que procesa tu /envio-publicitario/csv
    const csvContent = `nombre,apellido_paterno,apellido_materno,telefono
Samuel,Plata,,5611680933
`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'plantilla-envio.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-xl max-w-2xl mx-auto mt-12 text-white">
      <h2 className="text-3xl font-semibold mb-6 flex items-center gap-3 text-white">
        <Upload className="w-7 h-7 text-[var(--acc1)]" />
        Envío Masivo de WhatsApp
      </h2>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="flex justify-between items-center">
          <label className="text-white/60 text-sm font-medium">Sube tu CSV</label>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="text-white/60 text-sm flex items-center gap-1 hover:text-white underline"
          >
            <Download className="w-4 h-4" />
            Plantilla CSV
          </button>
        </div>

        <div className="bg-white/5 rounded-lg px-4 py-3 border border-white/10">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-white text-sm focus:outline-none"
          />
          {file && (
            <div className="flex items-center justify-between gap-4 mt-2 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {file.name}
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-red-400 hover:text-red-200 text-xs underline"
              >
                Eliminar archivo
              </button>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 text-[var(--acc1)] border border-[var(--acc1)]/30 font-bold py-2.5 px-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
          {loading ? 'Enviando...' : 'Enviar Mensajes'}
        </button>

        {mensaje && (
          <div
            className={`text-sm font-medium text-center flex items-center justify-center gap-2 mt-4 esté bien
              ${
              mensaje.startsWith('✅') ? 'text-green-400' : 'text-red-400'
            }`}
          >
            <AlertCircle className="w-4 h-4" />
            {mensaje}
          </div>
        )}
      </form>
    </div>
  );
}
